import { useConvex } from "convex/react";
import {
  type FunctionArgs,
  type FunctionReference,
  type FunctionReturnType,
  getFunctionName,
  makeFunctionReference,
} from "convex/server";
import { convexToJson, jsonToConvex, type Value } from "convex/values";
import { useEffect, useState } from "react";
import { isConvexError } from "@/lib/convex-errors";
import { breadcrumb } from "@/lib/observability";

/** The pause before each fresh attempt; its length is the retry budget. */
export const PLATFORM_RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000] as const;

type Outcome =
  | { requestKey: string; status: "value"; value: unknown }
  | { requestKey: string; status: "error"; error: Error };

/**
 * `useQuery` for a subscription the app cannot render without, retrying failures that carry
 * no `ConvexError` payload instead of throwing on the first one.
 *
 * Production clients see a Convex platform fault (overload, a timeout, a disabled deployment)
 * as a redacted "Server Error" with no payload. So does an uncoded bug in the function, which
 * this hook therefore retries too: the redacted error cannot tell the two apart, and the budget
 * is what keeps a real bug from looping. A `ConvexError` is the function's deliberate answer
 * (auth, coded app errors) and throws at once, as it would from `useQuery`; so does the failure
 * that spends the budget, so the nearest error boundary still reports a sustained outage.
 * While it retries, the hook keeps returning the last value this request received, or loading
 * (`undefined`) if it has none yet. Dropping to loading mid-session would tell every consumer
 * the data is gone: an organization id turning null unmounts every open project, and its
 * in-memory state with it, over an outage the next attempt recovers from. A platform failure
 * says nothing about the data having changed; a changed answer arrives as a result.
 *
 * Retrying means dropping the subscription and taking it again, which sends the query back to
 * the server under a new query id. Until the server's next transition the client still returns
 * the old failure for it, so only a plain failure delivered by the server after a subscription
 * starts counts as that attempt's outcome; one already cached when it starts is waited out.
 * A cached `ConvexError` is not: it throws at once. Consequently another component subscribed
 * to the same query and arguments keeps a plain failure alive, and this hook then waits for the
 * server to re-run the query, holding the value it has or reporting loading if it has none.
 *
 * Skipping, or changing the query or its arguments, ends the current attempt, its pause and the
 * value it held, and the next request starts with the whole budget and no value.
 */
export function useQueryWithPlatformRetry<Query extends FunctionReference<"query">>(
  query: Query,
  args: FunctionArgs<Query> | "skip"
): FunctionReturnType<Query> | undefined {
  const convex = useConvex();
  // `api.*` references are fresh objects on every access, so the request is keyed on the
  // function's name and the serialized arguments, never on the reference or args identity.
  const queryName = getFunctionName(query);
  const argsJson = args === "skip" ? null : JSON.stringify(convexToJson(args));
  const requestKey = argsJson === null ? null : `${queryName}:${argsJson}`;
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    if (argsJson === null || requestKey === null) return;
    const reference = makeFunctionReference<"query">(queryName);
    const queryArgs = jsonToConvex(JSON.parse(argsJson)) as Record<string, Value>;
    let failedAttempts = 0;
    let retryTimer: number | undefined;
    let unsubscribe: (() => void) | null = null;

    const subscribe = () => {
      const watch = convex.watchQuery(reference, queryArgs);
      const read = (deliveredByServer: boolean) => {
        let value: unknown;
        try {
          value = watch.localQueryResult();
        } catch (caught) {
          const error = caught instanceof Error ? caught : new Error(String(caught));
          // A ConvexError is the function's answer whenever it was computed, so a cached one
          // throws as it would from `useQuery`. A plain failure cached before this
          // subscription started may be the attempt it replaces, so only a delivered one counts.
          if (isConvexError(error)) {
            setOutcome({ requestKey, status: "error", error });
            return;
          }
          if (!deliveredByServer) return;
          if (failedAttempts >= PLATFORM_RETRY_DELAYS_MS.length) {
            setOutcome({ requestKey, status: "error", error });
            return;
          }
          const delayMs = PLATFORM_RETRY_DELAYS_MS[failedAttempts];
          failedAttempts += 1;
          breadcrumb(
            "convex.query.platform_failure_retry",
            { query: queryName, attempt: failedAttempts, delayMs },
            { category: "network", level: "warning" }
          );
          unsubscribe?.();
          unsubscribe = null;
          retryTimer = window.setTimeout(subscribe, delayMs);
          return;
        }
        if (value === undefined) return;
        if (failedAttempts > 0) {
          breadcrumb(
            "convex.query.platform_failure_recovered",
            { query: queryName, retries: failedAttempts },
            { category: "network" }
          );
          failedAttempts = 0;
        }
        setOutcome({ requestKey, status: "value", value });
      };
      unsubscribe = watch.onUpdate(() => read(true));
      read(false);
    };

    subscribe();
    return () => {
      window.clearTimeout(retryTimer);
      unsubscribe?.();
      // The outcome belonged to this lifecycle. Returning later to the same request (after a
      // skip, or A → B → A) starts pending unless the client itself still holds a value, so a
      // result the client has already evicted is never shown again.
      setOutcome(null);
    };
  }, [argsJson, convex, queryName, requestKey]);

  // Between a request change and the old lifecycle's cleanup, the stored outcome is still the
  // old request's; the key keeps that render from showing it.
  const current = outcome?.requestKey === requestKey ? outcome : null;
  if (current?.status === "error") {
    throw current.error;
  }
  return current?.status === "value" ? (current.value as FunctionReturnType<Query>) : undefined;
}
